import { useCallback } from 'react';
import { isAxiosError } from 'axios';
import { useSnackbar as useNotistackToast } from 'notistack';

import { STATUS_CODE, type StatusCode, t } from '@kc-console/core';

import { TOKEN_SCOPE, type TokenScope } from '../constants';

import type { CustomToastOptions } from '@kc-console/common-ui';
import type { OptionsObject, SnackbarKey, SnackbarMessage, VariantType } from 'notistack';

export type EnqueueToastOptions = OptionsObject<VariantType> & CustomToastOptions;

export type ErrorMessageMap = {
  [status in StatusCode]?: string;
};

export type EnqueueErrorToastOptions = {
  /**
   * error 객체를 전달합니다.
   * error 객체가 AxiosError 타입이고, error.response.status 값이 존재할 경우 errorMessageMap에서 맵핑되는 메시지를 자동으로 출력합니다.
   * 존재하지 않을 경우, enqueueErrorToast 첫 번째 인자로 넘긴 message를 출력합니다.
   */
  error?: unknown;
  /**
   * error.response.status와 맵핑되는 상황별 메시지를 전달합니다.
   * 존재하지 않거나 맵핑되는 메시지가 없을 경우, enqueueErrorToast 첫 번째 인자로 넘긴 message를 출력합니다.
   * 존재할 경우, const errorMessageMap = { ...defaultErrorMessageMap, ...errorMessageMap }; 으로 병합됩니다.
   */
  errorMessageMap?: ErrorMessageMap;
  /**
   * error.response.status와 맵핑되는 상황별 메시지 map의 기본값을 설정하기 위한 scope 정보를 전달합니다.
   * scope을 null로 전달할 경우, enqueueErrorToast 첫 번째 인자로 넘긴 message를 출력합니다.
   * @default TOKEN_SCOPE.PROJECT
   * TOKEN_SCOPE.DOMAIN | TOKEN_SCOPE.UNSCOPE
   *  - STATUS_CODE.FORBIDDEN: 권한이 없습니다.\n조직 관리자, IAM 조직 관리자에게 문의해 주세요.
   *  - STATUS_CODE.NOT_FOUND: 잘못된 리소스에 대한 요청입니다. 올바른 리소스를 요청해 주세요.
   * TOKEN_SCOPE.PROJECT
   *  - STATUS_CODE.FORBIDDEN: 권한이 없습니다.\n프로젝트 관리자, IAM 프로젝트 관리자에게 문의해 주세요.
   *  - STATUS_CODE.NOT_FOUND: 잘못된 리소스에 대한 요청입니다. 올바른 리소스를 요청해 주세요.
   */
  scope?: TokenScope | null;
};

export const getDomainScopeDefaultErrorMessageMap = (): ErrorMessageMap => {
  return {
    [STATUS_CODE.NOT_FOUND]: t('ui:toast.error.bad_resource_msg'),
    [STATUS_CODE.FORBIDDEN]: t('common:common.permission.denied.org_admin'),
  };
};

export const getProjectScopeDefaultErrorMessageMap = (): ErrorMessageMap => {
  return {
    [STATUS_CODE.NOT_FOUND]: t('ui:toast.error.bad_resource_msg'),
    [STATUS_CODE.FORBIDDEN]: t('common:common.permission.denied.project_admin'),
  };
};

export const getUnscopeDefaultErrorMessageMap = (): ErrorMessageMap => {
  return {
    ...getDomainScopeDefaultErrorMessageMap(),
  };
};

const SCOPE_ERROR_MESSAGE_MAP: Record<string, () => ErrorMessageMap> = {
  [TOKEN_SCOPE.DOMAIN]: getDomainScopeDefaultErrorMessageMap,
  [TOKEN_SCOPE.PROJECT]: getProjectScopeDefaultErrorMessageMap,
  [TOKEN_SCOPE.UNSCOPE]: getUnscopeDefaultErrorMessageMap,
};

const getScopeDefaultErrorMessageMap = (scope: TokenScope | null | undefined): ErrorMessageMap => {
  if (scope === null) return {};
  return SCOPE_ERROR_MESSAGE_MAP[scope ?? TOKEN_SCOPE.PROJECT]?.() ?? {};
};

/**
 * @description
 * - useToast 훅은 토스트 관련 함수들을 제공하는 훅입니다.
 * - enqueueToast: 토스트를 여는 함수
 * - enqueueSuccessToast: 성공 토스트를 여는 함수
 * - enqueueErrorToast: 에러 토스트를 여는 함수
 * - closeToast: 토스트를 닫는 함수
 */
export const useToast = () => {
  const { enqueueSnackbar: enqueueNotistackToast, closeSnackbar: closeToast } = useNotistackToast();

  /**
   * @description
   * - enqueueToast 함수는 notistack의 enqueueSnackbar 함수를 래핑한 함수입니다.
   * - 토스트의 변수는 다음과 같습니다.
   * - message: 토스트의 메시지
   * - options: 토스트의 옵션
   * - options.variant: 토스트 타입 (info, error, success)
   * - options.withCloseButton: 토스트 닫기 버튼 표시 여부 (true, false) (default: true)
   * - options.autoHideDuration: 토스트 자동 닫힘 시간 (number, null) (default: 3000)
   * - options.preventDuplicate: 토스트 중복 표시 방지 여부 (true, false)
   * - options.action: 토스트 액션 버튼 컴포넌트 (ReactNode)
   * - options.textButtonProps: 토스트 텍스트 버튼 컴포넌트 옵션 (label: ReactNode, onClick: () => void)
   */
  const enqueueToast: (message: SnackbarMessage, options?: EnqueueToastOptions) => SnackbarKey =
    useCallback(
      (message, options) =>
        enqueueNotistackToast(message, {
          ...options,
          variant: !options?.variant ? 'info' : options.variant,
        }),
      [enqueueNotistackToast],
    );

  /**
   * @description
   * - enqueueSuccessToast 함수는 성공 토스트를 표시하는 함수입니다.
   * - 토스트의 메시지를 전달하면 자동으로 성공 토스트를 표시합니다.
   * - 성공 토스트는 variant: success 로 설정됩니다.
   */
  const enqueueSuccessToast: (message: SnackbarMessage) => SnackbarKey = useCallback(
    message => enqueueToast(message, { variant: 'success' }),
    [enqueueToast],
  );

  /**
   * @description
   * - enqueueErrorToast 함수는 에러 토스트를 표시하는 함수입니다.
   * - 토스트의 메시지를 전달하면 자동으로 에러 토스트를 표시합니다.
   * - 에러 토스트는 variant: error / preventDuplicate: true 로 설정됩니다.
   * - 에러 토스트는 error와 errorMessageMap 객체를 전달하면 자동으로 AxiosError 객체의 response.status 에 따라 에러 메시지를 표시합니다.
   */
  const enqueueErrorToast: (
    message: SnackbarMessage,
    options?: EnqueueErrorToastOptions,
  ) => SnackbarKey = useCallback(
    (message, options) => {
      let errorMessage = message;
      const { error, errorMessageMap: overrideErrorMessageMap, scope } = options || {};
      const defaultErrorMessageMap = getScopeDefaultErrorMessageMap(scope);
      const errorMessageMap = { ...defaultErrorMessageMap, ...overrideErrorMessageMap };

      if (error && isAxiosError(error) && error.response?.status) {
        const status = error.response.status as StatusCode;
        if (errorMessageMap[status]) {
          errorMessage = errorMessageMap[status];
        }
      }

      return enqueueToast(errorMessage, {
        variant: 'error',
        preventDuplicate: true,
      });
    },
    [enqueueToast],
  );

  return { enqueueToast, enqueueSuccessToast, enqueueErrorToast, closeToast };
};
